import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import PageContainer, { GapSize } from "../components/PageContainer";
import PageSection from "../components/PageSection";
import {
  TermsContent,
  PrivacyContent,
} from "../components/ToaAndReturnPolicyContent";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

type Policy = "terms" | "privacy" | "returns" | "shipping";

type PolicyPageProps = {
  policy: Policy;
};

const TITLES: Record<Policy, string> = {
  terms: "Terms and Conditions",
  privacy: "Privacy Policy",
  returns: "Returns & Cancellations",
  shipping: "Shipping",
};

const DESCRIPTIONS: Record<Policy, string> = {
  terms:
    "Terms and Conditions for shopping at The Americana Company — orders, payments, shipping, and more.",
  privacy:
    "How The Americana Company collects, uses, and protects your personal information.",
  returns:
    "Our returns and cancellation policy — free cancellation before production, and how to report a damaged or misprinted item.",
  shipping:
    "How shipping works at The Americana Company — made-to-order production, delivery estimates, and tracking.",
};

/** Plain-language returns & cancellation summary; the binding terms live in /terms §5. */
const ReturnsContent = () => (
  <>
    <h1 className="text-2xl">Returns &amp; Cancellations</h1>
    <h3>Cancelling an order</h3>
    <p>
      Every item we sell is custom-printed just for you, so orders can be
      canceled for a full refund only <strong>before production begins</strong>{" "}
      at our print partner. If you change your mind, cancel as soon as possible
      from the <Link to="/orders/cancel">order cancellation page</Link> or your
      order confirmation email — orders canceled within the first few minutes
      are always free. Once your item enters production it&apos;s being made
      specifically for you and can no longer be canceled.
    </p>
    <h3>Damaged, misprinted, or defective items</h3>
    <p>
      If your item arrives damaged, misprinted, or materially defective, submit
      a <Link to="/orders/return">return request</Link> or email{" "}
      <a href="mailto:support@americanaandcompany.com">
        support@americanaandcompany.com
      </a>{" "}
      within <strong>30 days of delivery</strong> with a description and a clear
      photo of the issue. Approved claims receive a replacement or a full
      refund.
    </p>
    <h3>Change of mind &amp; sizing</h3>
    <p>
      Because every item is made to order, we can&apos;t accept returns or
      exchanges for change of mind or incorrect size selection. Please review
      product descriptions and size information carefully before ordering.
    </p>
    <h3>Refund timing</h3>
    <p>
      Refunds for approved claims are issued to your original payment method
      within 7 business days of approval.
    </p>
    <p>
      The full, binding policy is in our{" "}
      <Link to="/terms">Terms and Conditions</Link> (Section 5).
    </p>
  </>
);

/** Plain-language shipping overview; binding terms live in /terms §4. */
const ShippingContent = () => (
  <>
    <h1 className="text-2xl">Shipping</h1>
    <h3>Made to order</h3>
    <p>
      Every product is printed on demand after you order it — nothing sits in a
      warehouse. Production typically takes a few business days before your
      order ships; you&apos;ll receive an email confirmation when your order is
      placed and another with tracking as soon as it ships.
    </p>
    {/* The announcement bar's "printed in the USA" claim links here — this is
        the disclosure that keeps it accurate. Don't remove it without also
        removing the banner claim. */}
    <h3>Where our products are made</h3>
    <p>
      Our designs are created in the United States and printed here by our
      domestic print partners. The blank garments themselves are{" "}
      <strong>imported</strong> — manufactured outside the United States and
      then printed here.
    </p>
    <h3>Where we ship</h3>
    <p>
      We currently ship within the <strong>United States</strong>. Shipping
      options and rates are shown at checkout before you pay.
    </p>
    <h3>Delivery estimates</h3>
    <p>
      Delivery timeframes shown at checkout are carrier estimates, not
      guarantees — carriers can be delayed by weather and other events outside
      our control. If your package seems lost, contact us at{" "}
      <a href="mailto:support@americanaandcompany.com">
        support@americanaandcompany.com
      </a>{" "}
      and we&apos;ll help track it down.
    </p>
    <h3>Address accuracy</h3>
    <p>
      Please double-check your shipping address at checkout — we print and ship
      to exactly what you enter, and orders sent to an address entered
      incorrectly can&apos;t be redirected once shipped.
    </p>
    <p>
      Track any order any time on the{" "}
      <Link to="/orders/track">order tracking page</Link>. The full, binding
      policy is in our <Link to="/terms">Terms and Conditions</Link> (Section
      4).
    </p>
  </>
);

const PolicyPage: React.FC<PolicyPageProps> = ({ policy }) => {
  useDocumentMeta({
    title: TITLES[policy],
    description: DESCRIPTIONS[policy],
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [policy]);

  return (
    <PageContainer hasPadding gapSize={GapSize.Small}>
      <PageSection className="flex justify-center py-4">
        <div className="policy-content max-w-3xl w-full">
          {policy === "terms" && <TermsContent />}
          {policy === "privacy" && <PrivacyContent />}
          {policy === "returns" && <ReturnsContent />}
          {policy === "shipping" && <ShippingContent />}
        </div>
      </PageSection>
    </PageContainer>
  );
};

export default PolicyPage;
